'use client';

import { Button } from '@app/app/_components/Button/Button';
import { Heading } from '@app/app/_components/Heading/Heading';
import { Message } from '@app/app/_components/Message/Message';
import { ArrowLeftIcon } from '@app/app/_icons/ArrowLeftIcon';
import { ArrowRightIcon } from '@app/app/_icons/ArrowRightIcon';
import { useTranslations } from 'next-intl';
import React, { ReactNode, useMemo } from 'react';
import { twJoin, twMerge } from 'tailwind-merge';
import { motion } from 'framer-motion';

export interface WizardSubStep {
    key: string;
    when?: () => boolean;
    title: ReactNode;
    body: ReactNode;
}

export interface WizardStep {
    label: ReactNode;
    hideStepLabel?: boolean;
    subSteps: WizardSubStep[];
}

interface Props {
    steps: WizardStep[];
    currentStepIndex: number;
    currentSubStepIndex: number;
    onSubmit?: () => void;
    onBack?: () => void;
    error?: string;
    footer?: ReactNode;
    submitButtonLabel?: string;
    isPercentageStepper?: boolean;
    maxProgressPercentage?: number;
    disabledButton?: boolean;
    isLoading?: boolean;
    noIndicators?: boolean;
    noBackButton?: boolean;
    noStepTitle?: boolean;
    footerClassName?: string;
    buttonClassName?: string;
    className?: string;
    bodyClassName?: string;
    noFooter?: boolean;
    contentFooter?: ReactNode;
    outerClassName?: string;
}

export function Wizard({
    steps,
    currentStepIndex,
    currentSubStepIndex,
    onSubmit,
    onBack,
    error,
    footer,
    submitButtonLabel,
    isPercentageStepper = false,
    maxProgressPercentage,
    disabledButton = false,
    isLoading = false,
    noIndicators = false,
    noStepTitle = false,
    noBackButton = false,
    footerClassName,
    buttonClassName,
    className,
    bodyClassName,
    noFooter = false,
    contentFooter,
    outerClassName,
}: Props) {
    const t = useTranslations();

    const totalSubSteps = steps.reduce((acc, step) => acc + step.subSteps.length, 0);

    const progressPercentage = useMemo(() => {
        let completedSubSteps = 0;

        for (let i = 0; i < currentStepIndex; i++) {
            completedSubSteps += steps[i].subSteps.length;
        }

        completedSubSteps += currentSubStepIndex + 1;

        const calculatedPercentage = Math.floor((completedSubSteps / totalSubSteps) * 100);
        return maxProgressPercentage != null
            ? Math.min(calculatedPercentage, maxProgressPercentage)
            : calculatedPercentage;
    }, [currentStepIndex, currentSubStepIndex, steps, totalSubSteps, maxProgressPercentage]);

    return (
        <div
            className={twMerge(
                'flex min-h-full flex-col items-stretch justify-start py-4 md:items-center md:px-8 md:py-8',
                outerClassName,
            )}>
            <div className={twMerge('mb-32 flex flex-col gap-8 px-4 md:min-w-dialog md:max-w-dialog', className)}>
                {isPercentageStepper && !noIndicators && (
                    <div className="flex flex-row items-center gap-2">
                        <div className="h-2 w-full rounded-full bg-neutral-300">
                            <motion.div
                                className="h-2 rounded-full bg-primary-400"
                                style={{ width: `${progressPercentage}%` }}
                                initial={{ width: 0 }}
                                animate={{ width: `${progressPercentage}%` }}
                                transition={{ duration: 0.5, ease: 'easeOut' }}
                            />
                        </div>
                        <motion.div
                            className="text-right text-sm text-neutral-600"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            key={progressPercentage}>
                            <motion.span
                                initial={{ opacity: 0.5 }}
                                animate={{ opacity: 1 }}
                                key={progressPercentage}
                                transition={{ duration: 0.3 }}>
                                {`${progressPercentage}%`}
                            </motion.span>
                        </motion.div>
                    </div>
                )}

                {!isPercentageStepper && !noIndicators && (
                    <div className="grid auto-cols-fr grid-flow-col gap-3">
                        {steps.map((step, index) => (
                            <React.Fragment key={index}>
                                {!step.hideStepLabel && (
                                    <div
                                        className={twJoin(
                                            'select-none border-b-3 py-3',
                                            index > currentStepIndex && 'border-neutral-300',
                                            index <= currentStepIndex && 'border-primary-500',
                                        )}>
                                        {step.label}
                                    </div>
                                )}
                            </React.Fragment>
                        ))}
                    </div>
                )}

                <div className="relative flex items-start gap-4">
                    {!noBackButton && (
                        <button type="button" className="p-2" onClick={onBack} aria-label={t('general.backButton')}>
                            <ArrowLeftIcon className="size-6" />
                        </button>
                    )}
                    {!noStepTitle && (
                        <Heading className="text-2xl md:text-3xl">
                            {steps[currentStepIndex].subSteps[currentSubStepIndex].title}
                        </Heading>
                    )}
                </div>

                {error != null && <Message>{error}</Message>}
                <div className={twMerge('relative', bodyClassName)}>
                    {steps.flatMap((step, index) =>
                        step.subSteps.map((subStep, subIndex) => {
                            const isHidden = index !== currentStepIndex || subIndex !== currentSubStepIndex;
                            return (
                                <div
                                    className={
                                        isHidden
                                            ? 'absolute left-0 top-0 hidden size-0 max-h-0 max-w-0 overflow-hidden'
                                            : ''
                                    }
                                    key={subStep.key}
                                    aria-hidden={isHidden}>
                                    {subStep.body}
                                </div>
                            );
                        }),
                    )}
                </div>
            </div>

            <div className="relative">{contentFooter}</div>

            {!noFooter && (
                <div className="fixed inset-x-0 bottom-0 z-10 flex w-full justify-stretch bg-white shadow-lg-up md:justify-center">
                    <div
                        className={twMerge(
                            'flex w-full grow items-center justify-start px-4 py-6 md:min-w-dialog md:max-w-dialog md:px-0',
                            footerClassName,
                        )}>
                        <div className="flex w-full grow justify-between">{footer}</div>
                        <Button
                            className={twJoin('shrink-0 px-14', buttonClassName)}
                            disabled={disabledButton}
                            isLoading={isLoading}
                            onClick={onSubmit}
                            rightAddon={<ArrowRightIcon className="size-4" />}>
                            {submitButtonLabel != null ? submitButtonLabel : t('general.next')}
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
}
